import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type { TimesheetsWeekResponse } from './contractsMirror.js';
import type { TimesheetsSearch } from './searchParams.js';

/**
 * TanStack Query hook for the COARSE timesheets week-grid endpoint (the strangler-fig +
 * coarse-endpoint proof, CARVE-07). One query key per `{week}` — navigating prev/next week triggers
 * exactly one refetch of `GET /api/v1/timesheets/week-grid?week=…`. The SERVER computes the enriched
 * per-employee grid + the running totals + the pending edit-requests + the active clocks (22-13); the
 * SPA only renders the returned response.
 *
 * THE GUARDRAIL (D-03 / T-22-payroll): every `hours` value in the response is the SERVER-COMPUTED,
 * lunch-adjusted number. This hook (and the view it feeds) NEVER recomputes lunch/overtime — the math
 * that drives payroll gross-pay + job profitability lives ONLY on the server.
 *
 * Every request goes through `apiFetch` (the only fetch path — `credentials:'include'` +
 * `X-Tenant-Subdomain` baked in, single-flight 401 refresh). The tenant is derived server-side from
 * the auth context; the SPA NEVER sends a tenant id param (threat T-22-tenant).
 */

/** Build the stable query key for a week (one cache entry per week). */
export function timesheetsQueryKey(search: TimesheetsSearch): readonly unknown[] {
  return ['timesheets', search.week ?? null] as const;
}

/** Build the coarse-endpoint request path with the active week (no tenant param, read-only). */
export function timesheetsRequestPath(search: TimesheetsSearch): string {
  const params = new URLSearchParams();
  if (search.week) params.set('week', search.week);
  const qs = params.toString();
  return qs ? `/api/v1/timesheets/week-grid?${qs}` : '/api/v1/timesheets/week-grid';
}

export function useTimesheetsQuery(search: TimesheetsSearch) {
  return useQuery<TimesheetsWeekResponse>({
    queryKey: timesheetsQueryKey(search),
    queryFn: () => apiFetchJson<TimesheetsWeekResponse>(timesheetsRequestPath(search)),
    // Hold the week you were looking at while the next one loads.
    //
    // There is one cache entry per week, so without this every prev/next click lands on a key with
    // no data, `isPending` goes true, and TimesheetsRoute replaces the WHOLE screen with a spinner —
    // including the prev/next buttons that were just clicked. On a one-person tenant the response is
    // instant and nothing shows; on a real crew the grid, the edit-request queue and the active
    // clocks make it long enough that the buttons visibly vanish and the week nav reads as broken.
    // Stepping BACK to a week you already visited stays instant either way, which is what made the
    // fault look intermittent rather than constant.
    placeholderData: keepPreviousData,
    // The on-clock roster is fed by `activeClocks` on THIS response, so the list of who is punched
    // in goes stale the moment somebody clocks out on their phone. Thirty seconds is slow enough to
    // be invisible on the network tab and fast enough that a supervisor is not looking at a name
    // that left twenty minutes ago. The elapsed timers tick client-side every second, so the panel
    // reads as live between refetches without asking the server anything.
    refetchInterval: 30_000,
  });
}
